import React, { useState } from "react";
import { Button, Container } from "react-bootstrap";
import { FiList, FiPlus } from "react-icons/fi";
import { GoTriangleRight } from "react-icons/go";
import RequirePermission from "@core/components/RequirePermission";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { ACTION_CODES } from "@features/entity/types/actionCodes";
import FinanceDeductibleTable from "@features/admin/finance/deductible/components/FinanceDeductibleTable";
import FinanceDeductibleFormModal from "@features/admin/finance/deductible/components/FinanceDeductibleFormModal";
import ReorderFinanceDeductiblesModal from "@features/admin/finance/deductible/components/ReorderFinanceDeductiblesModal";

const ManageFinanceDeductibles: React.FC = () => {
    const sid = useScopeId() ?? 0;
    const [showCreate, setShowCreate] = useState(false);
    const [showReorder, setShowReorder] = useState(false);
    const [displayOrderOpen, setDisplayOrderOpen] = useState(false);
    const [refreshSignal, setRefreshSignal] = useState(0);
    const refresh = () => setRefreshSignal((s) => s + 1);
    const hasScope = Number.isFinite(sid) && sid > 0;

    return (
        <Container fluid className="py-4" data-testid="manage-finance-deductibles-container">
            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-start gap-3 mb-4">
                <div className="ntcog-page-title-block mb-0 position-relative">
                    {hasScope && (
                        <RequirePermission mode="section" action={ACTION_CODES.UPDATE_FINANCE_DEDUCTIBLE} scopeType="NATION" scopeId={sid}>
                            <button
                                type="button"
                                onClick={() => setDisplayOrderOpen((o) => !o)}
                                className="border-0 bg-transparent p-0 text-muted position-absolute d-flex align-items-center"
                                style={{ left: "-1.25rem", top: "0.45rem" }}
                                aria-label="Display order"
                                aria-expanded={displayOrderOpen}
                                aria-controls="finance-deductibles-display-order-body"
                                title={displayOrderOpen ? "Hide display order" : "Show display order"}
                                data-testid="finance-deductibles-display-order-toggle"
                            >
                                <GoTriangleRight
                                    size={14}
                                    style={{
                                        transition: "transform 0.2s",
                                        transform: displayOrderOpen ? "rotate(90deg)" : "rotate(0deg)",
                                    }}
                                />
                            </button>
                        </RequirePermission>
                    )}
                    <h1 className="ntcog-page-heading mb-1" data-testid="manage-finance-deductibles-title">
                        Finance Deductibles
                    </h1>
                    <p className="text-muted mb-0">
                        Manage the deductibles that categories and allocation rules can configure
                    </p>
                    {displayOrderOpen && (
                        <div
                            id="finance-deductibles-display-order-body"
                            className="d-flex flex-wrap align-items-center gap-2 mt-2"
                            data-testid="finance-deductibles-display-order"
                        >
                            <span className="text-muted small">
                                Sets the order deductibles appear in category and allocation rule forms.
                            </span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="py-0 px-2"
                                style={{ fontSize: "0.75rem" }}
                                onClick={() => setShowReorder(true)}
                                data-testid="reorder-finance-deductibles-button"
                            >
                                <FiList size={12} className="me-1" />
                                Reorder
                            </Button>
                        </div>
                    )}
                </div>
                {hasScope && (
                    <RequirePermission mode="section" action={ACTION_CODES.CREATE_FINANCE_DEDUCTIBLE} scopeType="NATION" scopeId={sid}>
                        <Button variant="primary" onClick={() => setShowCreate(true)} data-testid="create-finance-deductible-button">
                            <FiPlus className="me-2" />
                            Create Deductible
                        </Button>
                    </RequirePermission>
                )}
            </div>

            <FinanceDeductibleTable showPagination refreshSignal={refreshSignal} />

            <FinanceDeductibleFormModal
                show={showCreate}
                onHide={() => setShowCreate(false)}
                deductible={null}
                onSuccess={refresh}
            />
            <ReorderFinanceDeductiblesModal show={showReorder} onHide={() => setShowReorder(false)} onSuccess={refresh} />
        </Container>
    );
};

export default ManageFinanceDeductibles;
